<template>
  <div class="table-body" :key="paginationKey">
    <!-- Contenedor de las tarjetas del top - ONLY VISIBLE ON DESKTOP, hidden in admin mode -->
    <div class="top-items-container" v-if="!isMobile && !adminMode">
      <!-- Desktop Layout (normal horizontal layout) -->
      <div class="desktop-layout">
        <TournamentPositionCard :name="topItems[1].username" :rank="2" :countryCode="countryCode(topItems[1])"
          :broker-account-id="maskBrokerAccount(topItems[1].brokerAccountIdentifier)"
          :show-broker-account="shouldShowBrokerAccountInPodium" :show-ib-number="shouldShowIbNumber"
          :ib-number="topItems[1].ibNumber ?? topItems[1].ib_number"
          :medalType ="$store.state.tournament?.tournamentSettings.medalType" :showMedalImages="showAwardMedals" />
        <TournamentPositionCard :name="topItems[0].username" :rank="1" :countryCode="countryCode(topItems[0])"
          :broker-account-id="maskBrokerAccount(topItems[0].brokerAccountIdentifier)"
          :show-broker-account="shouldShowBrokerAccountInPodium" :show-ib-number="shouldShowIbNumber"
          :ib-number="topItems[0].ibNumber ?? topItems[0].ib_number"
          :medalType ="$store.state.tournament?.tournamentSettings.medalType"  :showMedalImages="showAwardMedals"/>
        <TournamentPositionCard :name="topItems[2].username" :rank="3" :countryCode="countryCode(topItems[2])"
          :broker-account-id="maskBrokerAccount(topItems[2].brokerAccountIdentifier)"
          :show-broker-account="shouldShowBrokerAccountInPodium" :show-ib-number="shouldShowIbNumber"
          :ib-number="topItems[2].ibNumber ?? topItems[2].ib_number"
          :medalType ="$store.state.tournament?.tournamentSettings.medalType"  :showMedalImages="showAwardMedals"/>
      </div>
    </div>

    <!-- Mobile header for leaderboard -->
    <div v-if="isMobile" class="mobile-leaderboard-header">
      <div class="leaderboard-title-container">
        <i class="fa-regular fa-ranking-star mr-2"></i>
        <h2>{{ $t('leaderboard') }}</h2>
      </div>
      <p class="podium-note" v-if="items.length >= 3 && false">
        {{ $t('podiumNote') }}
      </p>
    </div>

    <!-- Admin toolbar + Search Bar -->
    <div class="search-container" :class="{ 'admin-toolbar-row': adminMode }" v-if="items.length > 0 && !isMobile">
      <div v-if="adminMode" class="toolbar-actions">
        <button class="toolbar-download-btn" @click="$emit('download-members')">
          {{ $t('downloadMembers') || 'Download Members' }}
          <i class="fa-regular fa-arrow-down-from-bracket ml-2"></i>
        </button>
        <button v-if="!tournamentEnded" class="toolbar-add-btn ml-3" @click="$emit('add-participant')">
          {{ $t('addParticipant') || 'Add Participant' }}
          <i class="fa-solid fa-plus ml-2"></i>
        </button>
      </div>
      <div class="search-input-wrapper">
        <i class="fa-regular fa-search search-icon"></i>
        <input type="text" v-model="searchQuery" @input="handleSearch" :placeholder="$t('searchParticipants')"
          class="search-input" />
        <button v-if="searchQuery" @click="clearSearch" class="clear-search-btn" :title="$t('clearSearch')">
          <i class="fa-regular fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Desktop Table View -->
    <div v-if="!isMobile" class="table-responsive">
      <table v-if="displayItems.length" :class="{ 'admin-table': adminMode }">
        <thead :style="adminMode ? adminHeaderStyle : {}">
          <tr>
            <th v-if="!adminMode">{{ $t('rank') }}</th>
            <th v-if="!adminMode && !showMinimalView"></th>
            <th v-if="shouldShowNameColumn && hasNameData && !showMinimalView">{{ $t('name') }}</th>
            <th v-if="adminMode">{{ $t('headers.email') }}</th>
            <th v-if="shouldShowAccountColumn">{{ $t('account_NewFrontPage') }}</th>
            <th v-if="shouldShowUsernameColumn && !showMinimalView">{{ $t('username') }}</th>
            <th v-if="shouldShowIbNumber">{{ $t('ibNumber') }}</th>
            <th v-if="shouldShowScore" class="ranking-header">
              {{ rankingHeader }}
              <span v-if="showProfitFormula" class="formula-container">
                {{ getFormulaDisplayText() }}
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="formula-info-icon" v-bind="attrs" v-on="on">
                      fa-regular fa-info-circle
                    </v-icon>
                  </template>
                  <span class="formula-tooltip">{{ getFormulaTooltipText() }}</span>
                </v-tooltip>
              </span>
            </th>
            <th v-if="shouldShowTradingVolUsdColumn">{{ $t('trading_Vol') }}</th>
            <th v-if="shouldShowScoreBracketColumn">{{ $t('trading_Vol_Lots') }}</th>
            <th v-if="shouldShowRegistrationDate">{{ $t('registrationDate') }}</th>
            <th v-if="adminMode">{{ $t('headers.status') }}</th>
            <th v-if="adminMode">{{ $t('headers.actions') }}</th>
            <th v-if="!adminMode && !showMinimalView && totalAwards > 0" class="hide-on-mobile">{{ $t('rankingLayout_prize') }}</th>
          </tr>
        </thead>
        <tbody>
          <!-- Iteración sobre las filas con sus detalles -->
          <template v-for="(item, index) in safePaginatedItems">
            <!-- Fila de datos -->
            <tr @click="expandable ? toggleDetails(getActualIndex(index)) : ''" :key="getItemKey(item, index)"
              :class="['expandable-row', { expanded: expandedIndex === getActualIndex(index), 'admin-row': adminMode }]">
              <!-- Rank + position change + profile: hidden in admin mode -->
              <td v-if="!adminMode" class="td-pos" style="padding-right: 0;">
                <p :class="['p-rank', getRankClass(getDisplayRank(item, index) + 1)]">{{ getDisplayRank(item, index) + 1
                }}</p>
              </td>
              <td v-if="!adminMode && !showMinimalView" class="td-profile">
                <a :href="`javascript:void(0)`" class="profile-link should-blur">
                  <img
                    :src="item.profilePicture || 'https://upload.wikimedia.org/wikipedia/commons/d/d3/User_Circle.png'"
                    class="profile-image">
                  <Flag class="flag " :code="countryCode(item)"></Flag>
                </a>
              </td>
              <!-- Name: shown in both modes -->
              <td v-if="shouldShowNameColumn && hasNameData && !showMinimalView" class="td-name"><span
                  class="should-blur">{{
                    item.firstName ?
                      item.firstName.split(' ')[0] : '---' }}</span></td>
              <!-- Email: admin mode only, placed after name (matches Figma column order) -->
              <td v-if="adminMode" class="td-email"><span class="email-text">{{ item.email || '---' }}</span></td>
              <!-- Account ID -->
              <td v-if="shouldShowAccountColumn" class="td-account"><span class="account-id">{{
                maskBrokerAccount(item.brokerAccountIdentifier) }}</span></td>
              <!-- Username -->
              <td v-if="shouldShowUsernameColumn && !showMinimalView" class="td-username"><span class="should-blur">{{
                item.username }}</span></td>
              <td v-if="shouldShowIbNumber" class="td-ib-number"><span class="should-blur">{{ item.ibNumber ?? item.ib_number ?? '---' }}</span></td>
              <!-- Score -->
              <td v-if="shouldShowScore" class="td-profit"><span class="profit-chip">{{ formatRankingValue(item.score) }}</span></td>
              <td v-if="shouldShowTradingVolUsdColumn"><span class="profit-chip">{{ formatTradingVolumeUsd(item.tradingVolumeUsd) }}</span></td>
              <td v-if="shouldShowScoreBracketColumn"><span class="profit-chip">{{ item.scoreBracket != null ? Number(item.scoreBracket).toFixed(2) : '-' }}</span></td>
              <!-- Registration Date -->
              <td v-if="shouldShowRegistrationDate" class="td-registration-date">{{ formatRegistrationDate(item.registration_date) }}</td>
              <!-- Status: admin mode only -->
              <td v-if="adminMode" class="td-status">
                <span v-if="item.status === 'ACTIVE'" class="status-tag status-active">
                  {{ $t('tournamentStatuses.active') || 'Active' }}
                </span>
                <span v-else-if="item.status === 'DISQUALIFIED'" class="status-tag-wrapper">
                  <span class="status-tag status-disqualified">
                    {{ $t('disqualified') || 'Disqualified' }}
                  </span>
                  <DisqualificationTooltip
                    :participant-id="item.participantId"
                    position="top"
                  >
                    <i class="fa-solid fa-circle-info status-info-icon"></i>
                  </DisqualificationTooltip>
                </span>
                <span v-else class="status-tag status-other">
                  {{ item.status }}
                </span>
              </td>
              <!-- Actions: admin mode only — 2 icons: disqualify/activate + journal -->
              <td v-if="adminMode" class="td-actions">
                <div class="admin-action-icons">
                  <button
                    v-if="item.status === 'ACTIVE'"
                    class="action-icon-btn disqualify-btn"
                    @click.stop="$emit('disqualify', item)"
                    :title="$t('disqualify') || 'Disqualify'"
                  >
                    <i class="fa-regular fa-user-xmark"></i>
                  </button>
                  <button
                    v-else-if="item.status === 'DISQUALIFIED'"
                    class="action-icon-btn activate-btn"
                    @click.stop="$emit('activate', item)"
                    :title="$t('activateUser') || 'Activate'"
                  >
                    <i class="fa-regular fa-user-plus"></i>
                  </button>
                  <button
                    class="action-icon-btn journal-btn"
                    @click.stop="$emit('see-journal', item)"
                    :title="$t('seeJournal') || 'See Journal'"
                  >
                    <i class="fa-regular fa-book-open"></i>
                  </button>
                </div>
              </td>
              <!-- Award: only in non-admin mode -->
              <td v-if="!adminMode && !showMinimalView && totalAwards > 0" class="td-award"><span
                  v-if="getDisplayRank(item, index) + 1 <= totalAwards && displayItems.indexOf(item) < totalAwards"
                  :class="['winning-chip', 'hide-on-mobile', getRankClass(getDisplayRank(item, index) + 1)]">
                  <p class="award-title">{{ truncate(getAwardTitle(getDisplayRank(item, index) + 1), 15) }}</p><i
                    class="fa-regular fa-medal"></i>
                </span></td>
            </tr>

            <!-- Fila de detalles (se expande o colapsa) -->
            <tr v-if="!showMinimalView && expandedIndex === getActualIndex(index)" :key="'expanded-' + getItemKey(item, index)" class="details-row" :class="{ 'admin-details-row': adminMode }">
              <td :colspan="adminColumnCount || 8">
                <div class="details-content">
                  <div v-if="loadingIndex === getActualIndex(index)" class="spinner"></div>
                  <div v-else>
                    <ParticipantDetailedInfo :unique-id="getItemKey(item, index)" />
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
        <tfoot>
          <tr class="empty-row">
            <td :colspan="adminColumnCount || 8"></td>
          </tr>
        </tfoot>
      </table>
      <h4 v-else class="no-content-txt">{{ searchQuery ? $t('noParticipantsFound') : $t('noParticipants') }}</h4>
    </div>

    <!-- Mobile Cards View -->
    <div v-else class="mobile-cards-container">
      <div v-if="displayItems.length" class="mobile-cards-list">
        <MobileTableItem v-for="(item, index) in visibleItems" :key="index" :item="item"
          :rank="getDisplayRank(item, index) + 1" :previous-place="item.previousPlace"
          :expanded="expandedIndex === index" :loading="loadingIndex === index" :country-code="countryCode(item)"
          :total-awards="totalAwards" :award-title="truncate(getAwardTitle(getDisplayRank(item, index) + 1), 15)"
          :ranking-type="$store.state.tournament.tournamentSettings.tournamentRankingType"
          :is-podium="getDisplayRank(item, index) < 3" :ranking-header="rankingHeader"
          :formatted-score="formatRankingValue(item.score)" :show-minimal-view="shouldShowMinimalViewForMobile"
          :broker-account-id="maskBrokerAccount(item.brokerAccountIdentifier || item.brokerAccountId)"
          :show-score-bracket="shouldShowScoreBracketColumn"
          :show-trading-vol-usd="shouldShowTradingVolUsdColumn"
          :show-score="shouldShowScore"
          :show-ib-number="shouldShowIbNumber"
          :ib-number="item.ibNumber ?? item.ib_number"
          :show-registration-date="shouldShowRegistrationDate"
          @toggle-details="toggleDetails(index)">
          <template v-slot:details>
            <ParticipantDetailedInfo :unique-id="getItemKey(item, index)" />
          </template>
        </MobileTableItem>
      </div>
      <h4 v-else class="no-content-txt">{{ searchQuery ? $t('noParticipantsFound') : $t('noParticipants') }}</h4>

      <!-- View More / Less Button for Mobile -->
      <div v-if="displayItems.length > initialVisibleCount" class="view-more-container">
        <button @click="toggleViewMore" :style="organizerColorStyle">
          {{ showAllItems ? $t('showLess') : $t('viewMore') }}
          <i :class="showAllItems ? 'fa-regular fa-minus' : 'fa-regular fa-plus'"></i>
        </button>
      </div>
    </div>

    <!-- Admin Pagination -->
    <div v-if="adminMode" class="admin-pagination" :class="{ 'has-pages': saferTotalPages > 1 }">
      <div v-if="saferTotalPages > 1" class="pagination-left"></div>
      <div v-if="saferTotalPages > 1" class="pagination-center">
        <div class="pagination-controls">
          <button class="page-nav-btn" @click="prevPage" :disabled="currentPage === 1">
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <template v-for="(page, idx) in visiblePages">
            <span v-if="page === '...'" :key="'ellipsis-' + idx" class="page-ellipsis">...</span>
            <button v-else :key="page"
              class="page-number-btn" :class="{ active: page === currentPage }"
              @click="goToPage(page)" :style="page === currentPage ? adminHeaderStyle : {}">
              {{ page }}
            </button>
          </template>
          <button class="page-nav-btn" @click="nextPage" :disabled="currentPage === saferTotalPages">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
      <div class="pagination-right">
        <span class="pagination-info">
          {{ paginationRangeStart }}-{{ paginationRangeEnd }} of {{ displayItems.length }}
        </span>
        <div class="items-per-page-selector">
          <select v-model="adminItemsPerPage" @change="onItemsPerPageChange">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Regular Pagination (non-admin) -->
    <div v-else-if="shouldShowPagination" class="pagination">
      <button @click="prevPage" :disabled="currentPage === 1" :style="currentPage === 1 ? '' : organizerColorStyle"><i
          class="fa-regular fa-chevron-left"></i></button>
      <span>{{ currentPage }}/{{ saferTotalPages }}</span>
      <button @click="nextPage" :disabled="currentPage === saferTotalPages"
        :style="currentPage === saferTotalPages ? '' : organizerColorStyle"><i
          class="fa-regular fa-chevron-right"></i></button>
    </div>
  </div>
</template>

<script>
import TournamentRepository from '../../repositories/tournament';
import ParticipantDetailedInfo from './components/ParticipantDetailedInfo.vue';
import TournamentPositionCard from './components/TournamentPositionCard.vue';
import MobileTableItem from './components/MobileTableItem.vue';
import DisqualificationTooltip from '@/components/DisqualificationTooltip.vue';
import CountriesData from "@/views/Register/countries";

export default {
  name: 'TableBody',
  components: {
    TournamentPositionCard,
    ParticipantDetailedInfo,
    MobileTableItem,
    DisqualificationTooltip,
  },
  data() {
    return {
      expandedIndex: null, // Indica qué fila está expandida
      loadingIndex: null, // Indica qué fila está cargando
      countries: CountriesData, // Datos de los países
      currentPage: 1, // Página actual
      totalAwards: this.$store.state.tournament.awards.length,
      activeSlide: 0, // For mobile carousel: 0 = 1st place, 1 = 2nd place, 2 = 3rd place
      isMobile: false,
      showAllItems: false, // New property to control showing all items
      initialVisibleCount: 5, // Number of items to show initially
      paginationKey: 0, // Add key for forcing re-renders
      searchQuery: '', // Search input value
      filteredItems: [], // Filtered participants based on search
      searchTimeout: null, // Debounce timeout for search
      adminItemsPerPage: 10, // Items per page for admin pagination
    };
  },
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [
        {
          rank: 1,
          profileImage: 'https://hub.swiset.com/wp-content/uploads/2019/12/Responsive.jpg',
          firstName: 'John',
          lastName: 'Doe',
          username: 'john_doe',
          profitPercentage: '10%',
          profitUSD: '$1000',
          medalClass: 'gold',
          countryCode: 'US',
          expandable: true
        },
        {
          rank: 2,
          profileImage: 'https://hub.swiset.com/wp-content/uploads/2019/12/Responsive.jpg',
          firstName: 'Jane',
          lastName: 'Smith',
          username: 'jane_doe',
          profitPercentage: '8%',
          profitUSD: '$800',
          medalClass: 'silver',
          countryCode: 'US',
          expandable: true
        },
        {
          rank: 3,
          profileImage: 'https://hub.swiset.com/wp-content/uploads/2019/12/Responsive.jpg',
          firstName: 'Jim',
          lastName: 'Beam',
          username: 'jim_beam',
          profitPercentage: '6%',
          profitUSD: '$600',
          medalClass: 'bronze',
          countryCode: 'US',
          expandable: false
        },
      ]
    },
    topCount: {
      type: Number,
      default: 3,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    expandable: {
      type: Boolean,
      default: true,
    },
    blurred: {
      type: Boolean,
      default: false,
    },
    currentParticipant: {
      type: Object,
      default: () => ({
        brokerAccount: {
          identifier: 'Swiset Wallet'
        }
      })
    },
    publicParticipant: {
      type: Object
    },
    adminMode: {
      type: Boolean,
      default: false,
    },
    tournamentEnded: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    effectiveItemsPerPage() {
      return this.adminMode ? this.adminItemsPerPage : this.itemsPerPage;
    },
    adminHeaderStyle() {
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      if (colors && colors.primary) {
        const cleanColor = colors.primary.replace('#', '');
        return {
          backgroundColor: `#${cleanColor}`,
          color: '#fff',
        };
      }
      const clubColor = this.$store.state.clubMember?.club?.primaryColor;
      if (clubColor) {
        const cleanColor = clubColor.replace('#', '');
        return {
          backgroundColor: `#${cleanColor}`,
          color: '#fff',
        };
      }
      return { backgroundColor: '#009F63', color: '#fff' };
    },
    // Calculate total visible columns for colspan
    adminColumnCount() {
      let count = 0;
      if (this.shouldShowNameColumn && this.hasNameData) count++;
      if (this.adminMode) count++; // email
      if (this.shouldShowAccountColumn) count++;
      if (this.shouldShowUsernameColumn) count++;
      if (this.shouldShowIbNumber) count++;
      if (this.shouldShowScore) count++;
      if (this.shouldShowTradingVolUsdColumn) count++;
      if (this.shouldShowScoreBracketColumn) count++;
      if (this.shouldShowRegistrationDate) count++;
      if (this.adminMode) count += 2; // status + actions
      if (!this.adminMode) count += 2; // rank + profile
      if (!this.adminMode && this.totalAwards > 0) count++; // awards
      return count || 8;
    },
    // Items to display (either filtered or original)
    displayItems() {
      if (this.searchQuery.trim()) {
        return this.filteredItems;
      }
      return this.items;
    },

    // Podium should always show the original top 3, regardless of search
    topItems() {
      // Always get the top 3 participants from the original items (not filtered)
      const realParticipants = this.items.slice(0, this.topCount);

      // Create array with 3 positions
      const result = [
        // Position 1 (index 0)
        realParticipants[0] || this.createMockParticipant(1),
        // Position 2 (index 1)
        realParticipants[1] || this.createMockParticipant(2),
        // Position 3 (index 2)
        realParticipants[2] || this.createMockParticipant(3)
      ];

      // Sort by podium order (2nd, 1st, 3rd)
      return result.sort((a, b) => {
        const podiumOrder = [2, 1, 3];
        return podiumOrder.indexOf(a.rank) - podiumOrder.indexOf(b.rank);
      });
    },
    // Safer pagination logic
    safePaginatedItems() {
      if (!this.displayItems || this.displayItems.length === 0) {
        return [];
      }

      // Ensure currentPage is within valid bounds
      const safePage = Math.max(1, Math.min(this.currentPage, this.saferTotalPages));

      const start = (safePage - 1) * this.effectiveItemsPerPage;
      const end = start + this.effectiveItemsPerPage;

      // Add safety check to prevent out of bounds
      const result = this.displayItems.slice(start, Math.min(end, this.displayItems.length));

      return result;
    },

    // Replace the original paginatedItems
    paginatedItems() {
      return this.safePaginatedItems;
    },

    // Safer total pages calculation
    saferTotalPages() {
      if (!this.displayItems || this.displayItems.length === 0) {
        return 1;
      }
      return Math.max(1, Math.ceil(this.displayItems.length / this.effectiveItemsPerPage));
    },

    // Replace the original totalPages
    totalPages() {
      return this.saferTotalPages;
    },

    paginationRangeStart() {
      return ((this.currentPage - 1) * this.effectiveItemsPerPage) + 1;
    },
    paginationRangeEnd() {
      return Math.min(this.currentPage * this.effectiveItemsPerPage, this.displayItems.length);
    },
    visiblePages() {
      const total = this.saferTotalPages;
      const current = this.currentPage;
      const pages = [];
      if (total <= 5) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        if (current > 3) pages.push('...');
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        if (current < total - 2) pages.push('...');
        pages.push(total);
      }
      return pages;
    },
    // Add computed property to control pagination visibility
    shouldShowPagination() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures || [];
      return this.totalPages > 1 && selectedFeatures.includes('TOURNAMENT_RANKING_FULL_PAGINATION');
    },

    shouldShowNameColumn() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_NAME');
    },

    shouldShowUsernameColumn() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_USERNAME');
    },

    shouldShowAccountColumn() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER');
    },

    // Trading Vol Lots: any tournament with feature TOURNAMENT_RANKING_AGG_VOLUME
    shouldShowScoreBracketColumn() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return false;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_AGG_VOLUME');
    },
    // Trading Vol (USD): only tournament 682 with feature
    shouldShowTradingVolUsdColumn() {
      const tournamentId = this.$store.state.tournament?.tournamentId;
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures) || tournamentId == 708) {
        return false;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_AGG_VOLUME');
    },

    shouldShowScore() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // Hide score only when TOURNAMENT_RANKING_HIDE_SCORE is in features; otherwise show
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return !selectedFeatures.includes('TOURNAMENT_RANKING_HIDE_SCORE');
    },

    shouldShowIbNumber() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return false;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_IB_NUMBER');
    },

    shouldShowRegistrationDate() {
      return this.adminMode && this.items && this.items.some(item => item.registration_date);
    },

    shouldShowBrokerAccountInPodium() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return false;
      }
      const hasNameFeature = selectedFeatures.includes('TOURNAMENT_RANKING_NAME');
      const hasAccountIdentifierFeature = selectedFeatures.includes('TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER');
      
      // Return true if name feature is NOT present but account identifier feature IS present
      return !hasNameFeature && hasAccountIdentifierFeature;
    },

    hasNameData() {
      // Check if any participant has firstName data
      return this.items && this.items.some(item => item.firstName && item.firstName.trim() !== '');
    },

    carouselStyle() {
      // Fix the transform calculation to properly center the current slide
      if (!this.isMobile) return {};
      return {
        transform: `translateX(-${this.activeSlide * 33.333}%)`
      };
    },
    visibleItems() {
      // This is now only used for mobile view, based on displayItems
      if (this.showAllItems) {
        return this.paginatedItems;
      }
      // Otherwise, just show the top initialVisibleCount items from displayItems
      return this.displayItems.slice(0, this.initialVisibleCount);
    },
    rankingHeader() {
      // Get the ranking type from store
      const rankingType = this.$store.state.tournament?.tournamentSettings?.tournamentRankingType || 'SWISET_RATIO';

      // Return the appropriate header based on the ranking type
      switch (rankingType) {
        case 'SWISET_RATIO':
          return this.$t('swisetRatio');
        case 'BALANCE':
          return this.$t('balance');
        case 'EQUITY_RT':
          return this.$t('equity');
        case 'PROFIT_PERCENTAGE':
          return this.$t('profit');
        case 'GROSS_LOSS':
          return this.$t('grossLoss');
        case 'PERFORMANCE_RATIO':
          return this.$t('performanceRatio');
        case 'EQUITY_ROI_PERCENTAGE':
          return this.$t('equityROIPercentage');
        case 'EQUITY_PERCENTAGE':
          return this.$t('equity');
        default:
          return this.$t('profit');
      }
    },
    showProfitFormula() {
      if (this.$store.state.tournament && (this.$store.state.tournament.tournamentId === 342 || this.$store.state.tournament.tournamentId === 401)) {
        return this.$t('ATFxTaiwanTournamentFormula');
      }
      return false;
    },
    showMinimalView() {
      // Check for tournamentOrganizerId = 137
      if (this.$store.state.tournament && (this.$store.state.tournament.tournamentOrganizerId === 137)) {
        return true;
      }

      // Check for clubMember.club.id = 137
      if (this.$store.state.clubMember &&
        this.$store.state.clubMember.club &&
        this.$store.state.clubMember.club.id === 137) {
        return true;
      }

      return false;
    },
    shouldShowMinimalViewForMobile() {
      if (this.showMinimalView) {
        return true;
      }

      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return false;
      }
      
      const hasNameFeature = selectedFeatures.includes('TOURNAMENT_RANKING_NAME');
      const hasUsernameFeature = selectedFeatures.includes('TOURNAMENT_RANKING_USERNAME');
      
      // Return true if both NAME and USERNAME features are NOT present
      return !hasNameFeature && !hasUsernameFeature;
    },
    organizerColorStyle() {
      if (this.$store.state.tournament &&
        this.$store.state.tournament.tournamentSettings &&
        this.$store.state.tournament.tournamentSettings.colors &&
        this.$store.state.tournament.tournamentSettings.colors.primary) {
        const color = this.$store.state.tournament.tournamentSettings.colors.primary;
        // Remove # if present and re-add it
        const cleanColor = color.replace('#', '');
        const fullColor = `#${cleanColor}`;
        return {
          backgroundColor: fullColor,
          borderColor: fullColor,
          color: this.getContrastYIQ(cleanColor) === 'black' ? '#000 !important' : '#fff!important'
        };
      }
      // Default style
      return {};
    },
    shouldMaskAccounts() {
      return this.$store.state.tournament && this.$store.state.tournament.tournamentId === 448;
    },
    selectedFeatures() {
      return this.$store.state.tournament?.selectedFeatures;
    },
    showAwardMedals() {
      const f = this.selectedFeatures;
      if (!f || !Array.isArray(f)) return true; // tournaments viejos
      return f.includes("TOURNAMENT_RANKING_AWARD");
    }
  },
  mounted() {
    // Initialize filtered items
    this.filteredItems = [];

    if (this.blurred) {
      setTimeout(() => {
        document.querySelectorAll(".should-blur").forEach((el) => {
          el.style.filter = 'blur(5px)';
        });
      }, 0);
    }

    // Check if mobile and set flag
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);

    // Clear search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  },
  methods: {
    getMedalType(){
      const medalType = this.$store.state.tournamentsettings.medalType
      return (medalType !== undefined) ? medalType : "default"
    },
    getRankClass(rank) {
      switch (rank) {
        case 1:
          return 'gold';
        case 2:
          return 'silver';
        case 3:
          return 'bronze';
        default:
          return 'normal';
      }
    },
    countryCode(participant) {
      let countryFound = this.countries.find((country) => country.name === participant.country);
      return (countryFound !== undefined) ? countryFound.code : null;
    },
    getAwardTitle(rank) {
      const award = this.$store.state.tournament.awards.find(award => award.rank === rank);
      return award ? award.title : this.$t('noAward');
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.resetExpandedState();
        this.forceRerender();
      }
    },
    nextPage() {
      if (this.currentPage < this.saferTotalPages) {
        this.currentPage++;
        this.resetExpandedState();
        this.forceRerender();
      }
    },
    // Add method to reset pagination safely
    resetPagination() {
      this.currentPage = 1;
      this.resetExpandedState();
    },

    // Add method to reset expanded state
    resetExpandedState() {
      this.expandedIndex = null;
      this.loadingIndex = null;
    },

    // Add method to force re-render
    forceRerender() {
      this.paginationKey += 1;
    },

    goToPage(page) {
      if (page >= 1 && page <= this.saferTotalPages) {
        this.currentPage = page;
        this.resetExpandedState();
        this.forceRerender();
      }
    },
    onItemsPerPageChange() {
      this.currentPage = 1;
      this.resetExpandedState();
      this.forceRerender();
    },
    // Add method to get actual index (considering pagination and search)
    getActualIndex(paginatedIndex) {
      const safePage = Math.max(1, Math.min(this.currentPage, this.saferTotalPages));
      return (safePage - 1) * this.effectiveItemsPerPage + paginatedIndex;
    },

    // Add method to get display rank (considering search results)
    getDisplayRank(item, index) {
      if (this.searchQuery.trim()) {
        // For search results, find the original rank from the full items array
        const originalIndex = this.items.findIndex(originalItem =>
          originalItem.participantId === item.participantId ||
          originalItem.userId === item.userId ||
          originalItem.brokerAccountIdentifier === item.brokerAccountIdentifier
        );
        return originalIndex !== -1 ? originalIndex : index;
      }
      return this.getActualIndex(index);
    },

    // Add method to generate unique keys for items
    getItemKey(item, index) {
      // Create a unique key combining item identifier and pagination state
      if (!item) {
        return `empty-${index}-${this.currentPage}-${this.paginationKey}`;
      }
      const baseKey = item.participantId || item.userId || item.brokerAccountIdentifier || item.username || `idx-${index}`;
      return `${baseKey}-${this.currentPage}-${this.paginationKey}`;
    },

    // Search functionality with debounce
    handleSearch() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set a new timeout for debounced search
      this.searchTimeout = setTimeout(() => {
        this.performSearch();
      }, 300); // 300ms debounce
    },

    performSearch() {
      if (!this.searchQuery.trim()) {
        this.filteredItems = [];
        this.resetPagination();
        return;
      }

      const query = this.searchQuery.toLowerCase().trim();

      this.filteredItems = this.items.filter(participant => {
        // Search by username (remove @ symbol if present for easier searching)
        const username = (participant.username || '').toLowerCase().replace('@', '');
        // Search by broker account identifier
        const brokerAccount = (participant.brokerAccountIdentifier || '').toLowerCase();
        // Search by email (if available for admins)
        const email = (participant.email || '').toLowerCase();
        // Search by first name
        const firstName = (participant.firstName || '').toLowerCase();
        // Search by last name
        const lastName = (participant.lastName || '').toLowerCase();
        // Search by full name (first + last)
        const fullName = `${firstName} ${lastName}`.trim();
        // Search by country
        const country = (participant.country || '').toLowerCase();
        // Search by broker name
        const brokerName = (participant.brokerName || '').toLowerCase();
        // Search by participant ID (for admin debugging)
        const participantId = (participant.participantId || '').toString().toLowerCase();

        // Also try to match partial words and handle special characters
        const searchTerms = [
          username,
          brokerAccount,
          email,
          firstName,
          lastName,
          fullName,
          country,
          brokerName,
          participantId
        ];

        return searchTerms.some(term => term.includes(query));
      });

      // Reset pagination when search results change
      this.resetPagination();
      this.resetExpandedState();
    },

    clearSearch() {
      this.searchQuery = '';
      this.filteredItems = [];
      this.resetPagination();
      this.resetExpandedState();

      // Clear any pending search timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
    },

    // Update toggleDetails to handle pagination correctly
    async toggleDetails(actualIndex) {
      // eslint-disable-next-line no-console
      console.log('toggleDetails called - expandable:', this.expandable, 'publicPerformance:', this.$store.state.tournament?.tournamentSettings?.publicPerformance);
      if (!this.expandable) return;

      if (this.expandedIndex === actualIndex) {
        this.expandedIndex = null;
      } else {
        this.expandedIndex = actualIndex;
        this.loadingIndex = actualIndex;
        await this.setDetailedInfo(actualIndex).then(() => {
          this.loadingIndex = null;
        });
      }
    },
    async setDetailedInfo(actualIndex) {
      // Find the actual item from the display items array (filtered or original)
      let currentItem = this.displayItems[actualIndex] || this.publicParticipant;

      const tournamentId = this.$store.state.tournament.tournamentId;
      const tournametStartDate = this.$store.state.tournament.startTime;
      const tournametEndDate = this.$store.state.tournament.endTime;
      const participantId = currentItem.participantId;

      await TournamentRepository.getParticipantAnalyticsMini(tournamentId, participantId, tournametStartDate, tournametEndDate ).then((response) => {
        this.$store.commit('updateDetailedInfo', {
          ...response.data.content[0],
          lotsByAsset: response.data.content[0].lotsByAsset || []
        });
        // eslint-disable-next-line no-console
        console.log(response.data);
      }).catch(() => { });
      // eslint-disable-next-line no-console
      console.log(this.$store.state);
    },
    setParticipantEmbedRank() {
      let acc = this.$route.query.identifier;
      let participant = this.$store.state.tournament.participants.find(p => p.brokerAccountIdentifier === acc);
      this.publicParticipant = participant;
    },
    createMockParticipant(rank) {
      // Create a placeholder participant for empty positions
      return {
        rank: rank,
        profilePicture: null,
        firstName: '---',
        lastName: '---',
        username: `Position ${rank}`,
        country: null,
        score: null,
        previousPlace: null
      };
    },
    // Mobile carousel methods
    checkMobile() {
      this.isMobile = window.innerWidth <= 600;
    },

    prevSlide() {
      this.activeSlide = (this.activeSlide - 1 + 3) % 3;
    },

    nextSlide() {
      this.activeSlide = (this.activeSlide + 1) % 3;
    },

    goToSlide(index) {
      this.activeSlide = index;
    },
    toggleViewMore() {
      this.showAllItems = !this.showAllItems;

      // If closing the list, ensure that any expanded item outside of the visible range is collapsed
      if (!this.showAllItems && this.expandedIndex >= this.initialVisibleCount) {
        this.expandedIndex = null;
      }

      // Scroll to the button when toggling
      this.$nextTick(() => {
        const button = document.querySelector('.view-more-btn');
        if (button) {
          button.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    formatRegistrationDate(dateStr) {
      if (!dateStr) return '---';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString(this.$i18n.locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } catch {
        return '---';
      }
    },
    formatTradingVolumeUsd(value) {
      if (value == null) return '-';
      const num = Number(value);
      if (Number.isNaN(num)) return '-';
      return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    },
    formatRankingValue(value) {
      if (value === null || !this.shouldShowScore) return '-';

      if (this.showProfitFormula) {
        return `${(value * 100).toFixed(2)}%`;
      }

      const rankingType = this.$store.state.tournament?.tournamentSettings.tournamentRankingType || 'SWISET_RATIO';

      // Format the value based on the ranking type
      switch (rankingType) {
        case 'BALANCE':
          // Format with thousands separator
          return new Intl.NumberFormat(this.$i18n.locale).format(value.toFixed(2));

        case 'PROFIT_PERCENTAGE':
        case 'PERFORMANCE_RATIO':
          // Format as percentage
          return `${value.toFixed(2)}%`;
        case 'EQUITY_PERCENTAGE':
        case 'EQUITY_ROI_PERCENTAGE':
          return `${(value * 100).toFixed(2)}%`;
        case 'SWISET_RATIO':
        case 'EQUITY_RT':
        case 'GROSS_LOSS':
        default:
          // Format as plain number
          return value.toFixed(2);
      }
    },
    getContrastYIQ(hexcolor) {
      if (!hexcolor) return 'black';

      // Remove hash if it exists
      hexcolor = hexcolor.replace('#', '');

      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);

      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      return (yiq >= 128) ? 'black' : 'white';
    },

    // Method to mask broker account identifiers for tournament 448
    maskBrokerAccount(accountId) {
      if (!this.shouldMaskAccounts || !accountId) {
        return accountId || '---';
      }

      const accountStr = accountId.toString();
      if (accountStr.length <= 4) {
        return accountStr; // If 4 digits or less, show as is
      }

      // Show only last 4 digits, mask the rest with asterisks
      const lastFour = accountStr.slice(-4);
      const maskedPart = '*'.repeat(Math.min(accountStr.length - 4, 8)); // Limit asterisks to 8 for readability
      return `${maskedPart}${lastFour}`;
    },

    // New method to get the display text (without parentheses content)
    getFormulaDisplayText() {
      if (!this.showProfitFormula) return '';

      const formula = this.showProfitFormula;
      // Extract text before the first opening parenthesis
      const beforeParentheses = formula.split('(')[0];
      const afterParentheses = beforeParentheses.replace('(', '').trim();
      return afterParentheses.trim();
    },

    // New method to get the tooltip text (content within parentheses)
    getFormulaTooltipText() {
      if (!this.showProfitFormula) return '';

      const formula = this.showProfitFormula;
      // Extract content between the outermost parentheses
      const match = formula.match(/\((.+)\)/);
      if (match && match[1]) {
        // Remove the extra opening parenthesis if it exists at the start
        return match[1].replace(/^\(/, '').trim();
      }
      return formula;
    },
    truncate(text, max = 35) {
      if (!text) return '';
      const s = String(text).trim();
      if (s.length <= max) return s;
      return s.slice(0, max - 1) + '…';
    },
  },
};
</script>



<style scoped lang="scss">
.table-body {
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: "Avenir", sans-serif;
}

/* Search Container Styles */
.search-container {
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  &.admin-toolbar-row {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 12px;

    .search-input-wrapper {
      margin-bottom: 0;
    }
  }
}

/* Admin toolbar actions (download members + add participant) */
.toolbar-actions {
  display: flex;
  align-items: center;
}

.toolbar-download-btn,
.toolbar-add-btn {
  display: flex;
  align-items: center;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-family: 'Aeonik', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  background-color: #ebebeb;
  color: #14161B;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: #ddd;
  }

  i {
    font-size: 13px;
  }
}


.search-input-wrapper {
  position: relative;
  width: 100%;
  max-width: 400px;
  margin-bottom: 10px;
}

.search-input {
  width: 100%;
  padding: 8px 45px 8px 45px;
  border: 1px solid #9A9A9A;
  color: var(--v-mainBlack-base);
  border-radius: 8px;
  font-size: 14px;
  font-family: "Avenir", sans-serif;
  background-color: var(--v-fullWhite-base);
  transition: all 0.3s ease;
  outline: none;

  &:focus {
    border-color: var(--v-mainGreen-base);
    box-shadow: 0 0 0 3px rgba(0, 159, 99, 0.1);
  }

  &::placeholder {
    color: #9a9a9a;
  }
}

.search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #9a9a9a;
  font-size: 16px;
  pointer-events: none;
}

.clear-search-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9a9a9a;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    color: var(--v-error-base);
  }

  i {
    font-size: 14px;
  }
}

/* Mobile search adjustments */
@media screen and (max-width: 768px) {
  .search-input-wrapper {
    max-width: 100%;
    margin: 0 10px 10px 10px;
  }

  .search-input {
    padding: 10px 40px 10px 40px;
    font-size: 16px;
    /* Prevent zoom on iOS */
  }
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  min-width: 500px;
  /* Minimum width to prevent too much squeezing */

  tfoot {
    .empty-row {
      height: 20px;
      background-color: transparent;
      border-top: 1px solid #9a9a9a;

      td {
        border: solid 1px #9a9a9a;
        border-style: solid none none none;
      }
    }
  }
}

/* Estilo para la imagen del perfil */
.profile-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

/* Estilo para los items del top */
.top-items-container {
  width: 100%;
  margin-bottom: 40px;
  position: relative;
}

/* Desktop layout */
.desktop-layout {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

/* Mobile layout */
.mobile-layout {
  position: relative;
  padding: 0 20px;
  margin-bottom: 30px;
  /* Add space for indicators */
}

/* Carousel styles */
.carousel-container {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.carousel-track {
  display: flex;
  transition: transform 0.5s ease;
  width: 100%;
  /* Changed from 300% to 100% */
}

.carousel-item {
  min-width: 33.333%;
  /* Use min-width instead of width */
  flex: 0 0 33.333%;
  /* Prevent shrinking */
  box-sizing: border-box;
  padding: 0 10px;
}

.mobile-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.8);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);

  &.prev {
    left: 5px;
  }

  &.next {
    right: 5px;
  }
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  margin-top: 15px;
  position: absolute;
  bottom: -25px;
  left: 0;
  right: 0;

  .indicator {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #9A9A9A;
    margin: 0 5px;
    cursor: pointer;

    &.active {
      background-color: var(--v-mainGreen-base);
    }
  }
}

/* Estilo para la fila de detalles */
.details-row {
  background-color: var(--v-fullWhite-base);

  td {
    &:first-of-type {
      border-bottom-left-radius: 10px;
      border-left: solid 1px #9a9a9a;
    }

    &:last-of-type {
      border-bottom-right-radius: 10px;
      border-right: solid 1px #9a9a9a;
    }
  }

  .details-content {
    padding: 20px;
  }
}

/* Estilo para las filas principales */
.expandable-row {
  cursor: pointer;

  td {
    padding: 10px 15px;
    border: solid 1px #9a9a9a;
    border-style: solid none none none;

    &:first-of-type {
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
      border-left: solid 1px #9a9a9a;
    }

    &:last-of-type {
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
      border-right: solid 1px #9a9a9a;
    }
  }
}

/* Specific selectors for first and last td in the LAST expandable-row inside tbody */
tbody {

  /* Try targeting the last expandable-row specifically */
  tr.expandable-row:nth-last-child(2) {

    td:first-child {
      /* Styles for first td of last expandable row - ready for your custom styles */
      border-bottom-left-radius: 0px;
    }

    td:last-child {
      /* Styles for last td of last expandable row - ready for your custom styles */
      border-bottom-right-radius: 0px;
    }
  }

}


.expandable-row.expanded {
  td {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom: none;
  }
}

/* Estilo para el contenido adicional que se expandirá */
.details-content {
  overflow: hidden;
  background-color: var(--v-fullWhite-base);
}

/* Spinner de carga */
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--v-mainGreen-base);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Transición para expandir */
.expand-enter-active,
.expand-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.expand-enter-to,
.expand-leave {
  max-height: 300px;
  /* Ajusta el valor según el contenido máximo esperado */
  opacity: 1;
}

/* Estilo para las columnas de la tabla */
th,
td {
  padding: 0px 15px;
}

thead {
  th {
    font-size: 14px;
    color: #9A9A9A;
    font-weight: normal;
    padding-top: 10px;
    padding-bottom: 20px;
  }
}

tbody {
  tr {
    background-color: var(--v-fullWhite-base);
  }

  td {
    font-size: 14px;
    color: var(--v-mainBlack-base);
    text-align: center;

    .p-rank {
      border-radius: 50%;
      text-align: center;
      min-width: 24px;
      min-height: 24px;
      width: auto;
      height: auto;
      padding: 2px 6px;
      margin: auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      font-size: 14px !important;
      aspect-ratio: 1;
      white-space: nowrap;

      &.gold {
        border: 1px solid #FFD700;
        color: #FFD700;
      }

      &.silver {
        border: 1px solid #C0C0C0;
        color: #C0C0C0;
      }

      &.bronze {
        border: 1px solid #CD7F32;
        color: #CD7F32;
      }

      &.normal {
        border: 1px solid var(--v-mainBlack-base);
        color: var(--v-mainBlack-base);
      }
    }

    .profit-chip {
      background-color: var(--v-progressBarBackground-base);
      ;
      padding: 5px 20px;
      border-radius: 20px;
    }

    &.td-profit {
      padding-left: 40px;
      padding-right: 40px;
    }

    .winning-chip {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 5px 10px;
      border-radius: 20px;
      border: 1px solid;
      width: 100%;
      justify-content: center;

      &.gold {
        color: #FFD700;
        border-color: #FFD700;
      }

      &.silver {
        color: #C0C0C0;
        border-color: #C0C0C0;
      }

      &.bronze {
        color: #CD7F32;
        border-color: #CD7F32;
      }

      &.normal {
        color: var(--v-mainBlack-base);
      }

      .award-title {
        white-space: nowrap;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 120px;
      }
    }

    .profile-link {
      position: relative;
    }

    .flag {
      position: absolute;
      bottom: 0;
      right: 0;
    }

    .account-id {
      background-color: var(--v-progressBarBackground-base);
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 13px;
    }

  }

}

#broker-app {
  .no-content-txt {
    font-family: "Avenir", sans-serif !important;
    font-size: 36px !important;
    color: #9A9A9A !important;
    margin-top: 20px;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }
}

.no-content-txt {
  font-family: "Avenir", sans-serif;
  font-size: 36px !important;
  color: #9A9A9A;
  margin-top: 20px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 24px;
  }
}

/* Estilo para la paginación */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  align-self: flex-end;

  button {
    background-color: var(--v-mainGreen-base);
    color: #fff;
    border: none;
    padding: 5px 10px;
    margin: 0 10px;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:disabled {
      background-color: #9a9a9a;
      cursor: not-allowed;
    }
  }

  span {
    font-size: 14px;
    color: #9a9a9a;
  }
}

/* Responsive styles */
@media screen and (max-width: 1300px) {
  .hide-on-mobile {
    display: none !important;
  }

  .top-items-container {
    margin-bottom: 40px;
  }

  .mobile-layout {
    position: relative;
    padding: 0;
    margin-bottom: 30px;
  }

  .carousel-container {
    width: 100%;
    overflow: hidden;
  }

  .carousel-track {
    display: flex;
    transition: transform 0.5s ease;
    width: 300%;
    /* Set proper width for 3 items */
  }

  .carousel-item {
    width: 33.333%;
    min-width: 33.333%;
    flex: 0 0 33.333%;
    display: flex;
    justify-content: center;
    padding: 0 5px;
    box-sizing: border-box;
  }

  /* ...existing mobile nav and indicator styles... */
}

/* Tablet specific styles */
@media screen and (min-width: 769px) and (max-width: 1300px) {
  .hide-on-mobile {
    display: none !important;
  }

  .td-profit {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }
}

/* Mobile Cards Styling */
.mobile-cards-container {
  width: 100%;
  padding: 0 0px;
}

.mobile-cards-list {
  display: flex;
  flex-direction: column;
}

/* Update pagination for mobile */
@media screen and (max-width: 768px) {
  .pagination {
    width: 100%;
    justify-content: center;
    margin-top: 30px;
  }
}

/* Mobile Leaderboard Header */
.mobile-leaderboard-header {
  width: 100%;
  text-align: center;

  .leaderboard-title-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 5px;

    i {
      font-size: 18px;
      color: var(--v-mainBlack-base);
      margin-right: 8px;
    }

    h2 {
      font-size: 20px !important;
      font-weight: bold;
      margin: 0;
    }
  }

  .podium-note {
    font-size: 14px;
    color: #666;
    font-style: italic;
  }
}

/* View more button styles */
.view-more-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 0px 0 0px 0;
}

.view-more-btn {
  background-color: var(--v-fullWhite-base);
  border: 1px solid transparent;
  color: var(--v-mainGreen-base);
  border-radius: 20px;
  padding: 8px 20px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  i {
    font-size: 12px;
  }
}

/* Mobile specific styles for view more button */
@media screen and (max-width: 768px) {
  .view-more-btn {
    width: 100%;
    max-width: 200px;
    justify-content: center;
  }
}

/* New styles for formula display */
.ranking-header {
  position: relative;

  .formula-container {
    display: inline-flex;
    align-items: center;
    margin-left: 8px;

    .formula-info-icon {
      margin-left: 4px;
      cursor: pointer;
      opacity: 0.7;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 1;
      }
    }
  }
}

.formula-tooltip {
  font-size: 12px;
  max-width: 200px;
  text-align: center;
  color: #fefefe !important;
  font-family: "Avenir", sans-serif;
  padding: 8px 12px;
  border-radius: 4px;
}

/* ============================================
   Admin Mode Table Styles
   ============================================ */

/* Admin table: colored header, clean rows */
table.admin-table {
  border-collapse: collapse;

  thead {
    th {
      color: #fff !important;
      font-weight: 600;
      font-size: 13px;
      padding: 12px 16px;
      border-bottom: none;
      white-space: nowrap;
    }

    /* Round left corner on first header cell */
    th:first-child {
      border-radius: 8px 0 0 0;
    }

    /* Round right corner on last header cell */
    th:last-child {
      border-radius: 0 8px 0 0;
    }
  }

  tbody {
    /* Override card-like row styling in admin mode */
    tr.expandable-row.admin-row {
      cursor: pointer;

      td {
        padding: 14px 16px;
        font-size: 14px;
        color: #333;
        vertical-align: middle;
        border: none;
        border-bottom: 1px solid #eee;
        border-radius: 0 !important;
      }

      &:hover {
        background-color: #fafafa;
      }

      &.expanded td {
        border-bottom: none;
        border-radius: 0 !important;
      }
    }

    /* Admin details row — no card borders, clean expansion */
    tr.admin-details-row {
      td {
        padding: 0;
        border: none;
        border-bottom: 1px solid #eee;
        border-radius: 0 !important;
        border-left: none;
        border-right: none;
      }

      .details-content {
        padding: 16px 20px 20px;
        background-color: #fafafa;
      }
    }
  }
}

.td-email {
  .email-text {
    font-size: 13px;
    color: #555;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
  }
}

.td-status {
  .status-tag-wrapper {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .status-tag {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 14px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;

    &.status-active {
      background-color: #d7ffed;
      color: #005931;
    }

    &.status-disqualified {
      background-color: #ffd7d8;
      color: #590001 !important;
    }

    &.status-other {
      background-color: #f5f5f5;
      color: #666;
    }
  }

  .status-info-icon {
    font-size: 14px;
    color: #999;
    cursor: help;

    &:hover {
      color: #590001;
    }
  }
}

.td-actions {
  text-align: center;

  .admin-action-icons {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .action-icon-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
    font-size: 16px;
    color: #888;

    &:hover {
      color: #333;
    }

    &.disqualify-btn:hover {
      color: #590001;
    }

    &.activate-btn:hover {
      color: #005931;
    }
  }
}

/* ============================================
   Admin Pagination — matches Figma design
   ============================================ */
.admin-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 12px 0;
  font-family: "Avenir", sans-serif;

  &.has-pages {
    justify-content: space-between;
  }

  .pagination-left {
    flex: 1;
  }

  .pagination-center {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
  }

  .pagination-right {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }

  .pagination-info {
    font-size: 13px;
    color: #999;
    white-space: nowrap;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .page-nav-btn {
    background: none;
    border: none;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #666;
    transition: color 0.2s ease;

    &:hover:not(:disabled) {
      color: #333;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    i {
      font-size: 14px;
    }
  }

  .page-number-btn {
    background: none;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    transition: all 0.2s ease;

    &:hover:not(.active) {
      background-color: #f0f0f0;
    }

    &.active {
      color: #fff;
      font-weight: 600;
      border-radius: 50%;
    }
  }

  .page-ellipsis {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #999;
  }

  .items-per-page-selector {
    select {
      padding: 6px 10px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 13px;
      font-family: "Avenir", sans-serif;
      color: #666;
      background-color: transparent;
      cursor: pointer;
      outline: none;
      min-width: 55px;
      appearance: auto;

      &:focus {
        border-color: #aaa;
      }
    }
  }
}
</style>

<style lang="scss">
/* Dark mode overrides (unscoped so .theme--dark ancestor selector works) */
.theme--dark {
  .toolbar-download-btn,
  .toolbar-add-btn {
    background-color: #303740;
    color: #fff;

    &:hover {
      background-color: #3a424d;
    }
  }

  table.admin-table tbody {
    tr.expandable-row.admin-row {
      &:hover {
        background-color: #2a2f36;
      }

      td {
        border-bottom-color: #3a3f47;
      }
    }

    tr.admin-details-row {
      .details-content {
        background-color: #2a2f36;
      }

      td {
        border-bottom-color: #3a3f47;
      }
    }
  }
}
</style>
